import { useEffect } from 'react';

import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';

/**
 * Presence heartbeat. While an authenticated user has the app open, this stamps
 * `user_profiles.last_seen_at = now()` via the scoped `touch_last_seen` RPC every
 * ~45s (and immediately when a backgrounded tab returns to the foreground). The
 * admin "currently online" view reads that column (online = seen in the last 5
 * min). This is the standard low-maintenance presence pattern — far simpler than
 * Realtime Presence channels, and it persists "last active" for free.
 *
 * Mount it once high in the tree (ProtectedRoute) so it covers every authed
 * route. A module-level throttle makes it safe even if mounted more than once or
 * re-mounted on navigation — writes never fire more often than every 30s.
 */
const HEARTBEAT_MS = 45_000;
const MIN_GAP_MS = 30_000;
let lastPingAt = 0;

async function ping(): Promise<void> {
  const now = Date.now();
  if (now - lastPingAt < MIN_GAP_MS) return;
  lastPingAt = now;
  const { error } = await supabase.rpc('touch_last_seen');
  if (error) {
    // Best-effort: a missed heartbeat just briefly delays the online signal.
    logger.error('presence heartbeat failed', error);
    lastPingAt = 0; // allow the next tick to retry sooner
  }
}

export function usePresenceHeartbeat(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    void ping();
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') void ping();
    }, HEARTBEAT_MS);
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void ping();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [enabled]);
}
